import MapCanvas from '@/features/map-editor/components/MapCanvas';
import ShapeProperties from '@/features/map-editor/components/ShapeProperties';
import Toolbar from '@/features/map-editor/components/Toolbar';
import {
  selectBackgroundImage,
  selectCanvasDimensions,
  selectSelectedShapeId,
  selectShapes,
  selectTool,
  selectZoom,
} from '@/features/map-editor/mapEditorSelectors';
import { markAsSaved, reset, setLayoutData } from '@/features/map-editor/mapEditorSlice';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { formatEventDate, formatEventTime } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { Event, LayoutData, Seat } from '@eventflow/shared-types';
import { BarChart3, EyeOff, Search, MapPin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function MapEditorPage() {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { success, error: showError, info } = useToast();

  const shapes = useAppSelector(selectShapes);
  const dimensions = useAppSelector(selectCanvasDimensions);
  const backgroundImage = useAppSelector(selectBackgroundImage);
  const tool = useAppSelector(selectTool);
  // selectedShapeId se usa en ShapeProperties, no aquí directamente
  useAppSelector(selectSelectedShapeId);
  const zoom = useAppSelector(selectZoom);

  const [event, setEvent] = useState<Event | null>(null);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [isStatsMinimized, setIsStatsMinimized] = useState(true); // Inicia contraído en mobile-first

  // Helper: determina si una shape representa una mesa válida (excluye overlays)
  const isTableShape = useCallback((shape: any) => {
    const isOverlay = (shape as any).category === 'overlay';
    const hasTableIdentifier =
      Boolean((shape as any).tableId) || Boolean((shape as any).seatIdentifier);
    return !isOverlay && hasTableIdentifier;
  }, []);

  // Conteo de mesas para cabecera (excluye overlays y shapes sin identificador)
  const tableCountDisplay = useMemo(
    () => shapes.filter(isTableShape).length,
    [shapes, isTableShape]
  );

  // Calcular estadísticas (excluir overlays)
  const stats = useMemo(() => {
    const tableShapes = shapes.filter(isTableShape);
    let totalSeats = 0;
    let blockedTables = 0;
    let blockedSeats = 0;
    let hiddenTables = 0;

    for (const shape of tableShapes) {
      const capacity = Number((shape as any).capacity) || 12;
      totalSeats += capacity;

      if (shape.blocked) {
        blockedTables += 1;
        blockedSeats += capacity;
      }

      if ((shape as any).hidden === true) {
        hiddenTables += 1;
      }
    }

    return {
      totalTables: tableShapes.length,
      totalSeats,
      blockedTables,
      blockedSeats,
      hiddenTables,
    };
  }, [shapes]);

  // Cargar evento y layout al montar
  useEffect(() => {
    console.log('[MapEditor] Component mounted, eventId:', eventId);

    if (!eventId) {
      console.log('[MapEditor] No eventId found');
      setError('ID de evento no válido');
      setIsLoading(false);
      return;
    }

    const loadEvent = async () => {
      try {
        console.log('[MapEditor] Loading event with ID:', eventId);
        setIsLoading(true);
        const eventData = await api.get<Event>(`/events/${eventId}`);
        console.log('[MapEditor] Event loaded:', eventData);
        setEvent(eventData);

        // Cargar seats para mostrar colores reales en exportaciones
        const seatsData = await api.get<Seat[]>(`/events/${eventId}/seats`);
        console.log('[MapEditor] Seats loaded:', seatsData.length);
        setSeats(seatsData);

        // Si hay layout_data, cargarlo en el editor
        if (eventData.layout_data) {
          console.log('[MapEditor] ========== CARGANDO LAYOUT ==========');
          console.log(
            ' [MapEditor] Layout_data completo:',
            JSON.stringify(eventData.layout_data, null, 2)
          );
          const hiddenCount =
            eventData.layout_data.shapes?.filter((s: any) => s.hidden === true).length || 0;
          console.log(
            ` [MapEditor] Mesas ocultas en layout_data: ${hiddenCount} de ${eventData.layout_data.shapes?.length || 0}`
          );

          // Verificar una shape específica (TABLE-137)
          const table137 = eventData.layout_data.shapes?.find(
            (s: any) => s.tableId === 'TABLE-137' || s.seatIdentifier === 'TABLE-137'
          );
          if (table137) {
            console.log(
              ' [MapEditor] Shape TABLE-137 encontrada:',
              JSON.stringify(table137, null, 2)
            );
            console.log(
              ' [MapEditor] ¿Tiene campo hidden?',
              'hidden' in table137,
              'Valor:',
              table137.hidden
            );
          } else {
            console.log('[MapEditor] Shape TABLE-137 NO encontrada en layout_data');
          }

          // Contar mesas bloqueadas antes de normalizar
          const blockedCount =
            eventData.layout_data.shapes?.filter((s: any) => s.blocked === true).length || 0;
          console.log(
            ` [MapEditor] Mesas bloqueadas en layout_data: ${blockedCount} de ${eventData.layout_data.shapes?.length || 0}`
          );

          // Normalizar: si los campos hidden o blocked no existen, asumir false
          // Solo las mesas ocultas/bloqueadas tienen hidden: true o blocked: true en el JSON
          const normalizedLayout: LayoutData = {
            ...eventData.layout_data,
            shapes:
              eventData.layout_data.shapes?.map((shape: any) => {
                // Normalizar hidden: solo true si existe y es true
                const hiddenValue = shape.hidden === true;

                // Normalizar blocked: solo true si existe y es true
                // Si el fill es rojo (#ef4444) pero blocked no está presente, restaurar blocked: true
                const isRed = shape.fill === '#ef4444' || shape.fill === 'ef4444';
                const blockedValue = shape.blocked === true || (isRed && shape.blocked !== false);

                const normalizedShape: any = {
                  ...shape,
                  hidden: hiddenValue,
                  blocked: blockedValue,
                };

                // Si el shape está bloqueado pero el fill no es rojo, restaurar el fill rojo
                if (blockedValue && !isRed) {
                  normalizedShape.fill = '#ef4444';
                }

                return normalizedShape;
              }) || [],
          };

          const normalizedHiddenCount = normalizedLayout.shapes.filter(
            (s: any) => s.hidden === true
          ).length;
          const normalizedBlockedCount = normalizedLayout.shapes.filter(
            (s: any) => s.blocked === true
          ).length;

          if (normalizedHiddenCount !== hiddenCount) {
            console.warn(
              ` [MapEditor] Normalización hidden: ${hiddenCount} -> ${normalizedHiddenCount} mesas ocultas`
            );
          }
          if (normalizedBlockedCount !== blockedCount) {
            console.warn(
              ` [MapEditor] Normalización blocked: ${blockedCount} -> ${normalizedBlockedCount} mesas bloqueadas`
            );
            if (normalizedBlockedCount > blockedCount) {
              console.log(
                ` [MapEditor] Se restauraron ${normalizedBlockedCount - blockedCount} mesas bloqueadas por color rojo`
              );
            }
          }

          dispatch(setLayoutData(normalizedLayout));
        } else {
          // Layout vacío por defecto
          dispatch(
            setLayoutData({
              shapes: [],
              dimensions: { width: 1200, height: 800 },
              metadata: {
                version: '1.0',
              },
            })
          );
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error loading event:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar el evento');
        setIsLoading(false);
      }
    };

    loadEvent();

    // Limpiar el estado del editor al desmontar
    return () => {
      dispatch(reset());
    };
  }, [eventId, dispatch]);

  // Guardar layout en el backend
  const handleSave = useCallback(
    async (isAutoSave = false) => {
      if (!eventId) return;

      try {
        setIsSaving(true);

        // Normalizar shapes: solo incluir hidden: true y blocked: true para las que están activos
        // No incluir false porque Supabase puede eliminarlo del JSONB
        const normalizedShapes = shapes.map((shape: any) => {
          const normalizedShape: any = { ...shape };
          const isOverlay = (shape as any).category === 'overlay';
          if (isOverlay) {
            // Limpiar campos no relevantes para overlays
            delete normalizedShape.seatIdentifier;
            delete normalizedShape.tableId;
            delete normalizedShape.tableLabel;
            delete normalizedShape.capacity;
            // Eliminar price si existe (p.ej. price: 0)
            if ('price' in normalizedShape) {
              delete normalizedShape.price;
            }
          }

          // Normalizar hidden: solo incluir si es true
          if (shape.hidden === true) {
            normalizedShape.hidden = true;
          } else {
            delete normalizedShape.hidden;
          }

          // Normalizar blocked: solo incluir si es true
          if (shape.blocked === true) {
            normalizedShape.blocked = true;
          } else {
            delete normalizedShape.blocked;
          }

          return normalizedShape;
        });

        const layoutData: LayoutData = {
          shapes: normalizedShapes,
          dimensions,
          backgroundImage: backgroundImage || undefined,
          metadata: {
            version: '1.0',
            lastModified: new Date().toISOString(),
          },
        };

        const hiddenCount = normalizedShapes.filter((s: any) => s.hidden === true).length;
        const blockedCount = normalizedShapes.filter((s: any) => s.blocked === true).length;
        console.log(
          `💾 [MapEditor] Guardando layout con ${hiddenCount} mesas ocultas, ${blockedCount} mesas bloqueadas de ${normalizedShapes.length}`
        );

        if (hiddenCount > 0) {
          const hiddenIds = normalizedShapes
            .filter((s: any) => s.hidden === true)
            .map((s: any) => s.tableId || s.seatIdentifier);
          console.log('💾 [MapEditor] IDs ocultos:', hiddenIds);
          const sampleHidden = normalizedShapes.find((s: any) => s.hidden === true);
          if (sampleHidden) {
            console.log(
              '💾 [MapEditor] Ejemplo de shape oculta a guardar:',
              JSON.stringify(sampleHidden, null, 2)
            );
          }
        }

        if (blockedCount > 0) {
          const blockedIds = normalizedShapes
            .filter((s: any) => s.blocked === true)
            .map((s: any) => s.tableId || s.seatIdentifier);
          console.log('💾 [MapEditor] IDs bloqueados:', blockedIds);
          const sampleBlocked = normalizedShapes.find((s: any) => s.blocked === true);
          if (sampleBlocked) {
            console.log(
              '💾 [MapEditor] Ejemplo de shape bloqueada a guardar:',
              JSON.stringify(sampleBlocked, null, 2)
            );
            console.log(
              '💾 [MapEditor] ¿Tiene campo blocked?',
              'blocked' in sampleBlocked,
              'Valor:',
              sampleBlocked.blocked
            );
          }
        } else {
          console.log('[MapEditor] No hay mesas bloqueadas en el layout a guardar');
        }

        await api.put(`/events/${eventId}/map`, { layoutData });

        dispatch(markAsSaved());

        // Sincronizar automáticamente los asientos en la BD
        // Esto actualiza el status de los asientos cuando se bloquean/desbloquean mesas
        try {
          console.log('[MapEditor] Sincronizando asientos con la BD...');
          const syncResult = await api.post<{ created: number; updated: number; skipped: number }>(
            `/seats/generate/${eventId}`
          );
          console.log(
            ` [MapEditor] Asientos sincronizados: ${syncResult.created} creados, ${syncResult.updated} actualizados`
          );

          // Recargar seats para actualizar los colores en el editor
          const updatedSeats = await api.get<Seat[]>(`/events/${eventId}/seats`);
          setSeats(updatedSeats);

          if (!isAutoSave) {
            if (syncResult.updated > 0) {
              success(` Mapa guardado y ${syncResult.updated} asientos sincronizados`);
            } else {
              success(' Mapa guardado exitosamente');
            }
          }
        } catch (syncErr) {
          console.warn('[MapEditor] Error sincronizando asientos:', syncErr);
          // No bloquear el guardado si falla la sincronización
          if (!isAutoSave) {
            success(' Mapa guardado. Ejecuta "Recalcular Asientos" para sincronizar.');
          }
        }

        console.log(isAutoSave ? ' Auto-guardado exitoso' : ' Guardado manual exitoso');
      } catch (err) {
        console.error('Error saving map:', err);
        if (!isAutoSave) {
          showError(
            'Error al guardar el mapa: ' +
              (err instanceof Error ? err.message : 'Error desconocido')
          );
        }
      } finally {
        setIsSaving(false);
      }
    },
    [eventId, shapes, dimensions, backgroundImage, dispatch, success, showError]
  );

  // Limpiar asientos viejos de la base de datos
  // NOTA: La confirmación de seguridad ahora está en Toolbar.tsx
  const handleCleanupSeats = async () => {
    if (!eventId) return;

    try {
      setIsGenerating(true);

      const response = await api.post<{ deleted: number }>(`/seats/cleanup/${eventId}`);

      success(`Limpieza completada: ${response.deleted} asientos eliminados`);
      info('Ahora ejecuta "Recalcular Asientos" para sincronizar el mapa.');
    } catch (err) {
      console.error('Error cleaning up seats:', err);
      showError(
        'Error al limpiar asientos: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Generar asientos en la base de datos
  // NOTA: La confirmación de seguridad ahora está en Toolbar.tsx
  const handleGenerateSeats = async () => {
    if (!eventId || !shapes || shapes.length === 0) {
      showError('Agrega mesas al mapa antes de generar asientos');
      return;
    }

    try {
      setIsGenerating(true);

      const response = await api.post<{ created: number; updated: number; skipped: number }>(
        `/seats/generate/${eventId}`
      );

      success(
        `Generación completada: ${response.created} creados, ${response.updated} actualizados`
      );
    } catch (err) {
      console.error('Error generating seats:', err);
      showError(
        'Error al generar asientos: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerar todo: limpiar + generar (para eventos sin ventas)
  const handleRegenerateAll = async () => {
    if (!eventId || !shapes || shapes.length === 0) {
      showError('Agrega mesas al mapa antes de regenerar asientos');
      return;
    }

    // Primera confirmación
    if (
      !confirm(
        `REGENERAR TODOS LOS ASIENTOS\n\n` +
          `ADVERTENCIA: Esta acción es IRREVERSIBLE.\n\n` +
          `Esto es para eventos NUEVOS sin ventas.\n\n` +
          `Pasos a realizar:\n` +
          `1. Eliminar TODOS los asientos existentes\n` +
          `2. Crear asientos nuevos desde el mapa (${shapes.filter(isTableShape).length} mesas)\n\n` +
          `¿Deseas continuar?`
      )
    ) {
      return;
    }

    // Segunda confirmación (doble verificación)
    if (
      !confirm(
        `SEGUNDA CONFIRMACIÓN REQUERIDA\n\n` +
          `Estás a punto de ELIMINAR TODOS los asientos existentes\n` +
          `y crear nuevos desde el mapa.\n\n` +
          `Esta acción NO se puede deshacer.\n\n` +
          `¿Estás SEGURO que deseas continuar?`
      )
    ) {
      return;
    }

    try {
      setIsGenerating(true);

      // Paso 1: Limpiar TODO con deleteAll=true
      await api.post(`/seats/cleanup/${eventId}`, { deleteAll: true });

      // Paso 2: Generar asientos nuevos
      const response = await api.post<{ created: number; updated: number; skipped: number }>(
        `/seats/generate/${eventId}`
      );

      const tableCount = shapes.filter(isTableShape).length;
      success(
        ` Regeneración completa: ${response.created} asientos creados para ${tableCount} mesas`
      );
    } catch (err) {
      console.error('Error regenerating seats:', err);
      showError(
        'Error al regenerar asientos: ' +
          (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-guardado cada 30 segundos
  // TEMPORALMENTE DESHABILITADO para debugging del campo hidden
  // useEffect(() => {
  //   if (!eventId || !shapes || shapes.length === 0 || isLoading) return;

  //   const autoSaveInterval = setInterval(() => {
  //     handleSave(true); // true = auto-save silencioso
  //   }, 30000); // 30 segundos

  //   return () => clearInterval(autoSaveInterval);
  // }, [eventId, shapes, dimensions, isLoading, handleSave]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-lg font-medium text-gray-700">Cargando evento...</div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-lg font-medium text-red-600 mb-4">
            {error || 'Evento no encontrado'}
          </div>
          <button onClick={() => navigate('/dashboard/events')} className="btn-secondary">
            ← Volver a Mis Eventos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header - Compacto en móvil (portrait y landscape) */}
      <div className="bg-white border-b border-gray-200 px-2 md:px-6 py-1.5 md:py-4 flex items-center justify-between gap-2 md:gap-4 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => navigate('/dashboard/events')}
            className="text-[10px] md:text-sm text-gray-600 hover:text-gray-900 mb-0.5 md:mb-2 block"
          >
            ← <span className="hidden md:inline">Volver a Mis Eventos</span>
            <span className="md:hidden">Volver</span>
          </button>
          <h1 className="text-sm md:text-2xl font-bold text-gray-900 truncate">{event.name}</h1>
          <p className="text-xs md:text-sm text-gray-600 hidden md:block">
            Editor de Mapa de Mesas
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] md:text-sm text-gray-600 whitespace-nowrap font-medium">
            {tableCountDisplay} mesa{tableCountDisplay !== 1 ? 's' : ''}
          </div>
          {event.event_date && (
            <div className="text-[10px] md:text-xs text-gray-500 whitespace-nowrap hidden md:block">
              {formatEventDate(event.event_date, { includeWeekday: false })}
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <Toolbar
        onSave={() => handleSave(false)}
        onGenerateSeats={handleGenerateSeats}
        onCleanupSeats={handleCleanupSeats}
        onRegenerateAll={handleRegenerateAll}
        isSaving={isSaving || isGenerating}
        eventId={eventId}
      />

      {/* Main content: Canvas + Properties */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-auto relative">
          <MapCanvas
            seats={seats}
            eventInfo={
              event
                ? {
                    title: event.name,
                    venue: event.venue_name,
                    address: event.venue_address,
                    date: event.event_date
                      ? formatEventDate(event.event_date, { includeTime: false })
                      : undefined,
                    time: event.event_date ? formatEventTime(event.event_date) : undefined,
                  }
                : undefined
            }
          />

          {/* Stats Panel - Compacto en móvil (portrait y landscape) */}
          <div className="absolute top-2 md:top-4 left-2 md:left-4 z-50 pointer-events-none">
            <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-md px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm space-y-0.5 md:space-y-1 min-w-[100px] md:min-w-[200px] max-w-[140px] md:max-w-[300px] pointer-events-auto">
              <button
                onClick={() => setIsStatsMinimized(!isStatsMinimized)}
                className="absolute top-1 md:top-2 right-1 md:right-2 text-gray-400 hover:text-gray-600 transition-colors z-20 bg-white rounded p-0.5"
                title={isStatsMinimized ? 'Expandir estadísticas' : 'Minimizar estadísticas'}
              >
                {isStatsMinimized ? (
                  <svg
                    className="w-3 h-3 md:w-4 md:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-3 h-3 md:w-4 md:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                )}
              </button>

              <div className="pr-4 md:pr-6 space-y-0.5 md:space-y-1">
                {/* Versión móvil compacta (portrait y landscape) */}
                <p className="text-gray-600 text-[10px] md:hidden">
                  <span className="font-semibold">{stats.totalTables}</span> mesas ·{' '}
                  <span className="font-semibold">{stats.totalSeats}</span> asientos
                </p>
                {/* Versión desktop completa */}
                <p className="text-gray-600 hidden md:block">
                  <span className="font-semibold">{stats.totalTables}</span> mesas
                </p>
                <p className="text-gray-500 text-xs hidden md:block flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  Total de asientos: <span className="font-semibold">{stats.totalSeats}</span>
                </p>
                {!isStatsMinimized && (
                  <>
                    {stats.blockedTables > 0 && (
                      <>
                        {/* Móvil (portrait y landscape) */}
                        <p className="text-gray-500 text-[10px] md:hidden">
                           <span className="text-red-600 font-medium">{stats.blockedTables}</span>{' '}
                          bloq. (<span className="text-red-600">{stats.blockedSeats}</span>)
                        </p>
                        {/* Desktop */}
                        <p className="text-gray-500 text-xs hidden md:block">
                           Mesas bloqueadas:{' '}
                          <span className="font-semibold text-red-600">{stats.blockedTables}</span>
                        </p>
                        <p className="text-gray-500 text-xs hidden md:block">
                           Asientos bloqueados:{' '}
                          <span className="font-semibold text-red-600">{stats.blockedSeats}</span>
                        </p>
                      </>
                    )}
                    {stats.hiddenTables > 0 && (
                      <>
                        <p className="text-gray-500 text-[10px] md:hidden flex items-center gap-1">
                          <EyeOff className="w-3 h-3" />
                          <span className="text-purple-600 font-medium">{stats.hiddenTables}</span>{' '}
                          ocultas
                        </p>
                        <p className="text-gray-500 text-xs hidden md:block flex items-center gap-1">
                          <EyeOff className="w-3 h-3" />
                          Mesas ocultas:{' '}
                          <span className="font-semibold text-purple-600">
                            {stats.hiddenTables}
                          </span>
                        </p>
                      </>
                    )}
                    <p className="text-gray-500 text-xs hidden md:block">
                      {tool === 'select' ? 'Click para seleccionar' : `Click para agregar ${tool}`}
                    </p>
                    <p className="text-gray-500 text-xs hidden md:block flex items-center gap-1">
                      <Search className="w-3 h-3" />
                      Zoom: {(zoom * 100).toFixed(0)}%
                    </p>
                    <p className="text-gray-500 text-xs hidden md:block flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Click en fondo + drag para mover
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <ShapeProperties />
      </div>
    </div>
  );
}


